import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API } from "../../services/api";

const SIDEBAR_WIDTH_STORAGE_KEY = "commande-label-sidebar-width";
const DEFAULT_SIDEBAR_WIDTH = 216;
const MIN_SIDEBAR_WIDTH = 168;
const MAX_SIDEBAR_WIDTH = 420;

function clampSidebarWidth(value) {
    return Math.min(Math.max(Number(value) || DEFAULT_SIDEBAR_WIDTH, MIN_SIDEBAR_WIDTH), MAX_SIDEBAR_WIDTH);
}

function storedSidebarWidth() {
    try {
        return clampSidebarWidth(globalThis.localStorage?.getItem(SIDEBAR_WIDTH_STORAGE_KEY));
    } catch {
        return DEFAULT_SIDEBAR_WIDTH;
    }
}

const DEFAULT_FORM = {
    name: "Nouveau dossier",
    color: "#17B978",
    parentId: "",
    targetType: "NONE",
    ehpadId: "",
    ehpadIds: [],
    tourneeId: "",
    createEhpadChildren: false,
};

const MAIL_FILTER_LABELS = {
    READ: "Mails lus",
    UNREAD: "Mails non lus",
    FAVORITE: "Mails favoris",
    NOT_FAVORITE: "Mails non favoris",
};

const STATUS_FILTER_LABELS = {
    COMMAND_TO_VALIDATE: "À valider",
    COMMAND_VALIDATED: "Validées",
};

const FILTER_LABELS = { ...MAIL_FILTER_LABELS, ...STATUS_FILTER_LABELS };

const DEFAULT_LABEL_NAMES = {
    NONE: "Nouveau dossier",
    ...MAIL_FILTER_LABELS,
    ...STATUS_FILTER_LABELS,
};

function buildTree(labels) {
    const byParent = new Map();
    labels.forEach(label => {
        const key = label.parentId ?? null;
        byParent.set(key, [...(byParent.get(key) || []), label]);
    });
    return byParent;
}

function submitButtonLabel(saving, editingId, creatingMultipleEhpads) {
    if (saving) return "Enregistrement…";
    if (editingId) return "Enregistrer";
    return creatingMultipleEhpads ? "Créer les libellés" : "Créer le libellé";
}

function countTitle(label) {
    if (label.filterType === "COMMAND_VALIDATED") {
        return "Compteur non affiché pour les commandes validées";
    }
    if (label.filterType === "READ") {
        return "Compteur non affiché pour les commandes lues";
    }
    const count = label.pendingCount || 0;
    const noun = count === 1 ? "commande" : "commandes";
    if (label.filterType === "UNREAD") return `${count} ${noun} non lue${count === 1 ? "" : "s"} à valider`;
    return `${count} ${noun} à valider`;
}

function countDisplay(label) {
    return ["COMMAND_VALIDATED", "READ"].includes(label.filterType) ? "—" : label.pendingCount || 0;
}

function ehpadLabelOverrides(ehpad, multiple, form, sortOrder) {
    let name = form.name.trim();
    let color = form.color;
    if (multiple) {
        name = ehpad.code || ehpad.nom;
        color = ehpad.color || form.color;
    }
    return { name, color, ehpadId: ehpad.id, tourneeId: null, sortOrder };
}

async function createEhpadLabels(form, ehpads, labels, payloadFromForm) {
    const selectedEhpads = form.ehpadIds
        .map(id => ehpads.find(ehpad => String(ehpad.id) === id))
        .filter(Boolean);
    const multiple = selectedEhpads.length > 1;
    const responses = await Promise.all(selectedEhpads.map((ehpad, index) => API.post(
        "/commande-labels",
        payloadFromForm(ehpadLabelOverrides(ehpad, multiple, form, labels.length + index)),
    )));
    return responses[0]?.data;
}

async function saveLabel(editingId, payload) {
    if (editingId) {
        const response = await API.put(`/commande-labels/${editingId}`, payload);
        return response.data;
    }
    const response = await API.post("/commande-labels", payload);
    return response.data;
}

function LabelForm({
    editingId,
    ehpads,
    error,
    form,
    labels,
    onClose,
    onReset,
    onSubmit,
    saving,
    setForm,
    tournees,
}) {
    const cancel = editingId ? onReset : onClose;
    const creatingMultipleEhpads = !editingId && form.targetType === "EHPAD" && form.ehpadIds.length > 1;
    const changeTargetType = event => {
        const targetType = event.target.value;
        setForm(previous => ({
            ...previous,
            targetType,
            ehpadId: "",
            ehpadIds: [],
            tourneeId: "",
            name: editingId ? previous.name : DEFAULT_LABEL_NAMES[targetType] || "",
        }));
    };
    const changeEhpad = event => {
        const ehpadId = event.target.value;
        const selected = ehpads.find(ehpad => String(ehpad.id) === ehpadId);
        setForm(previous => ({
            ...previous,
            ehpadId,
            ehpadIds: ehpadId ? [ehpadId] : [],
            name: editingId || !selected ? previous.name : (selected.code || selected.nom),
            color: editingId || !selected?.color ? previous.color : selected.color,
        }));
    };
    const toggleEhpad = ehpadId => {
        setForm(previous => {
            const selectedIds = previous.ehpadIds.includes(ehpadId)
                ? previous.ehpadIds.filter(id => id !== ehpadId)
                : [...previous.ehpadIds, ehpadId];
            const soleEhpad = selectedIds.length === 1
                ? ehpads.find(ehpad => String(ehpad.id) === selectedIds[0])
                : null;
            return {
                ...previous,
                ehpadIds: selectedIds,
                ehpadId: soleEhpad ? selectedIds[0] : "",
                name: soleEhpad ? (soleEhpad.code || soleEhpad.nom) : previous.name,
                color: soleEhpad?.color || previous.color,
            };
        });
    };
    const changeTournee = event => {
        const tourneeId = event.target.value;
        const selected = tournees.find(tournee => String(tournee.id) === tourneeId);
        let suggestedName = "";
        if (selected) {
            suggestedName = /^tourn[ée]e\b/i.test(selected.name)
                ? selected.name
                : `Tournée ${selected.name}`;
        }
        setForm(previous => ({
            ...previous,
            tourneeId,
            name: editingId || !selected ? previous.name : suggestedName,
        }));
    };
    return (
        <form className="commande-label-form" onSubmit={onSubmit}>
            <h3>{editingId ? "Modifier le libellé" : "Informations du libellé"}</h3>
            <label>Type de libellé<select autoFocus value={form.targetType} onChange={changeTargetType}>
                <option value="NONE">Dossier d’organisation</option>
                <option value="EHPAD">Par EHPAD</option>
                <option value="TOURNEE">Par tournée</option>
                <option value="COMMAND_TO_VALIDATE">Commandes à valider</option>
                <option value="COMMAND_VALIDATED">Commandes validées</option>
                <option value="UNREAD">Mails non lus</option>
                <option value="READ">Mails lus</option>
                <option value="FAVORITE">Mails favoris</option>
                <option value="NOT_FAVORITE">Mails non favoris</option>
            </select></label>
            {form.targetType === "EHPAD" && (editingId ? (
                <label>EHPAD<select required value={form.ehpadId} onChange={changeEhpad}>
                    <option value="">Choisir un EHPAD</option>
                    {ehpads.map(ehpad => <option key={ehpad.id} value={ehpad.id}>{ehpad.code || ehpad.nom}</option>)}
                </select></label>
            ) : (
                <fieldset className="commande-label-ehpad-picker">
                    <legend>EHPAD</legend>
                    <p>Sélectionnez un ou plusieurs établissements.</p>
                    <div>
                        {ehpads.map(ehpad => {
                            const id = String(ehpad.id);
                            return (
                                <label key={ehpad.id}>
                                    <input
                                        type="checkbox"
                                        checked={form.ehpadIds.includes(id)}
                                        onChange={() => toggleEhpad(id)}
                                    />
                                    <span className="commande-label-ehpad-color" style={{ background: ehpad.color || "#17B978" }} aria-hidden="true" />
                                    <span title={ehpad.code || ehpad.nom}>{ehpad.code || ehpad.nom}</span>
                                </label>
                            );
                        })}
                    </div>
                </fieldset>
            ))}
            {form.targetType === "TOURNEE" &&
                <label>Tournée<select required value={form.tourneeId} onChange={changeTournee}>
                    <option value="">Choisir une tournée</option>
                    {tournees.map(tournee => <option key={tournee.id} value={tournee.id}>{tournee.name}</option>)}
                </select></label>}
            <label><span>Libellé parent <span className="commande-label-optional">(facultatif)</span></span><select value={form.parentId} onChange={event => setForm(previous => ({ ...previous, parentId: event.target.value }))}>
                <option value="">Aucun — niveau principal</option>
                {labels.filter(label => label.id !== editingId).map(label => <option key={label.id} value={label.id}>{label.name}</option>)}
            </select></label>
            {!creatingMultipleEhpads &&
                <label>Nom du libellé<input value={form.name} maxLength={80} onChange={event => setForm(previous => ({ ...previous, name: event.target.value }))} placeholder="Ex. Tournée du matin" /></label>}
            {creatingMultipleEhpads &&
                <p className="commande-label-ehpad-help">Chaque libellé reprendra le code et la couleur de son EHPAD.</p>}
            {form.targetType === "TOURNEE" && !editingId && <label className="commande-label-checkbox"><input type="checkbox" checked={form.createEhpadChildren} onChange={event => setForm(previous => ({ ...previous, createEhpadChildren: event.target.checked }))} /> Créer automatiquement un sous-libellé pour chaque EHPAD de la tournée</label>}
            {!creatingMultipleEhpads &&
                <label className="commande-label-color">Couleur<input type="color" value={form.color} onChange={event => setForm(previous => ({ ...previous, color: event.target.value }))} /></label>}
            {error && <p className="commande-label-error">{error}</p>}
            <div className="commande-label-form-actions">
                <button type="button" onClick={cancel}>Annuler</button>
                <button type="submit" disabled={saving}>{submitButtonLabel(saving, editingId, creatingMultipleEhpads)}</button>
            </div>
        </form>
    );
}

export default function CommandeLabelsSidebar({
    activeKey,
    onSelectLabel,
    onClearLabel,
    pharmacyId,
    page,
    totalPages,
    setPage,
    countsRefreshKey,
}) {
    const [labels, setLabels] = useState([]);
    const [expanded, setExpanded] = useState(new Set());
    const [modalMode, setModalMode] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(DEFAULT_FORM);
    const [ehpads, setEhpads] = useState([]);
    const [tournees, setTournees] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [sidebarWidth, setSidebarWidth] = useState(storedSidebarWidth);
    const [resizing, setResizing] = useState(false);
    const resizeStartRef = useRef(null);

    const updateSidebarWidth = value => setSidebarWidth(clampSidebarWidth(value));

    useEffect(() => {
        try {
            globalThis.localStorage?.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(sidebarWidth));
        } catch {
            // Le panneau reste redimensionnable si le stockage local est indisponible.
        }
    }, [sidebarWidth]);

    const loadLabels = useCallback(() => {
        Promise.resolve(API.get("/commande-labels", { noRedirectOn401: true }))
            .then(response => setLabels(response?.data || []))
            .catch(() => setLabels([]));
    }, []);

    useEffect(() => { loadLabels(); }, [loadLabels, pharmacyId, countsRefreshKey]);

    useEffect(() => {
        if (!modalMode) return;
        Promise.all([
            API.get("/ehpads", { noRedirectOn401: true }),
            API.get("/ehpads/tournees", { noRedirectOn401: true }),
        ]).then(([ehpadResponse, tourneeResponse]) => {
            setEhpads(ehpadResponse.data || []);
            setTournees(tourneeResponse.data || []);
        }).catch(() => setError("Impossible de charger les EHPAD et les tournées."));
    }, [modalMode]);

    const tree = useMemo(() => buildTree(labels), [labels]);

    const resetForm = (parentId = "") => {
        setEditingId(null);
        setForm({ ...DEFAULT_FORM, parentId: parentId ? String(parentId) : "" });
        setError("");
    };

    const closeModal = () => {
        setModalMode(null);
        resetForm();
    };

    const openCreateModal = () => {
        resetForm();
        setModalMode("create");
    };

    const openManageModal = () => {
        resetForm();
        setModalMode("manage");
    };

    const startEdit = label => {
        setEditingId(label.id);
        setForm({
            name: label.name,
            color: label.color || "#17B978",
            parentId: label.parentId ? String(label.parentId) : "",
            targetType: label.ehpadId ? "EHPAD" : label.tourneeId ? "TOURNEE" : label.filterType || "NONE",
            ehpadId: label.ehpadId ? String(label.ehpadId) : "",
            ehpadIds: label.ehpadId ? [String(label.ehpadId)] : [],
            tourneeId: label.tourneeId ? String(label.tourneeId) : "",
            createEhpadChildren: false,
        });
        setError("");
    };

    const payloadFromForm = (override = {}) => ({
        name: form.name.trim(),
        color: form.color,
        parentId: form.parentId ? Number(form.parentId) : null,
        ehpadId: form.targetType === "EHPAD" && form.ehpadId ? Number(form.ehpadId) : null,
        tourneeId: form.targetType === "TOURNEE" && form.tourneeId ? Number(form.tourneeId) : null,
        filterType: FILTER_LABELS[form.targetType] ? form.targetType : null,
        sortOrder: labels.length,
        ...override,
    });

    const save = async event => {
        event.preventDefault();
        if (!editingId && form.targetType === "EHPAD" && form.ehpadIds.length === 0) {
            return setError("Sélectionnez au moins un EHPAD.");
        }
        const creatingMultipleEhpads = !editingId && form.targetType === "EHPAD" && form.ehpadIds.length > 1;
        if (!creatingMultipleEhpads && !form.name.trim()) return setError("Saisissez un nom de libellé.");
        setSaving(true);
        setError("");
        try {
            let saved;
            if (!editingId && form.targetType === "EHPAD") {
                saved = await createEhpadLabels(form, ehpads, labels, payloadFromForm);
            } else {
                saved = await saveLabel(editingId, payloadFromForm());
            }
            if (!editingId && form.targetType === "TOURNEE" && form.createEhpadChildren && saved?.id) {
                const selectedTournee = tournees.find(tournee => Number(tournee.id) === Number(form.tourneeId));
                const children = ehpads.filter(ehpad => ehpad.tournee === selectedTournee?.name);
                await Promise.all(children.map((ehpad, index) => API.post("/commande-labels", {
                    name: ehpad.code || ehpad.nom,
                    color: ehpad.color || form.color,
                    parentId: saved.id,
                    ehpadId: ehpad.id,
                    tourneeId: null,
                    sortOrder: index,
                })));
                setExpanded(previous => new Set(previous).add(saved.id));
            }
            loadLabels();
            if (!editingId && modalMode === "create") closeModal();
            else resetForm();
        } catch (requestError) {
            setError(requestError.response?.data?.detail || requestError.response?.data?.message || "Enregistrement impossible.");
        } finally {
            setSaving(false);
        }
    };

    const remove = async label => {
        if (!globalThis.confirm("Voulez-vous supprimer ce libellé ?")) return;
        try {
            await API.delete(`/commande-labels/${label.id}`);
            if (activeKey === `label:${label.id}`) onClearLabel();
            loadLabels();
            if (editingId === label.id) resetForm();
        } catch {
            setError("Suppression impossible.");
        }
    };

    const toggleExpanded = id => setExpanded(previous => {
        const next = new Set(previous);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const selectLabel = (label, hasChildren) => {
        if (hasChildren) {
            setExpanded(previous => {
                const next = new Set(previous);
                if (activeKey === `label:${label.id}`) next.delete(label.id);
                else next.add(label.id);
                return next;
            });
        }
        if (activeKey === `label:${label.id}`) {
            onClearLabel();
            return;
        }
        onSelectLabel(label);
    };

    const renderLabels = (parentId = null, depth = 0) => (tree.get(parentId) || []).map(label => {
        const children = tree.get(label.id) || [];
        const isOpen = expanded.has(label.id);
        const canFilter = Boolean(label.ehpadId || label.tourneeId || label.filterType);
        const displayedName = label.ehpadCode || label.name;
        return (
            <React.Fragment key={label.id}>
                <div
                    className={`commande-label-row${activeKey === `label:${label.id}` ? " is-active" : ""}`}
                    style={{ paddingLeft: `${12 + depth * 17}px` }}
                >
                    {children.length > 0 ? (
                        <button className="commande-label-expander" onClick={() => toggleExpanded(label.id)} aria-label={isOpen ? `Replier ${label.name}` : `Déplier ${label.name}`}>
                            {isOpen ? "⌄" : "›"}
                        </button>
                    ) : <span className="commande-label-expander" aria-hidden="true" />}
                    <button
                        className="commande-label-main"
                        onClick={() => canFilter ? selectLabel(label, children.length > 0) : toggleExpanded(label.id)}
                        title={displayedName}
                        aria-label={displayedName}
                    >
                        <span className="commande-label-dot" style={{ background: label.color }} />
                        <span className="commande-label-name">{displayedName}</span>
                        <span
                            className="commande-label-count"
                            aria-hidden="true"
                            title={countTitle(label)}
                        >{countDisplay(label)}</span>
                    </button>
                </div>
                {isOpen && renderLabels(label.id, depth + 1)}
            </React.Fragment>
        );
    });

    const renderManagedLabels = (parentId = null, depth = 0) => (tree.get(parentId) || []).map(label => {
        const children = tree.get(label.id) || [];
        const isOpen = expanded.has(label.id);
        const displayedName = label.ehpadCode || label.name;
        const description = label.ehpadCode || label.ehpadName || label.tourneeName || FILTER_LABELS[label.filterType] || "Dossier";
        return (
            <React.Fragment key={label.id}>
                <div
                    className={`commande-label-manager-row${editingId === label.id ? " is-editing" : ""}`}
                    style={{ paddingLeft: `${10 + depth * 20}px` }}
                >
                    {children.length > 0 ? (
                        <button
                            type="button"
                            className="commande-label-expander commande-label-manager-expander"
                            onClick={() => toggleExpanded(label.id)}
                            aria-label={isOpen ? `Replier ${label.name}` : `Déplier ${label.name}`}
                        >
                            {isOpen ? "⌄" : "›"}
                        </button>
                    ) : <span className="commande-label-expander commande-label-manager-expander" aria-hidden="true" />}
                    <span className="commande-label-manager-dot" style={{ background: label.color }} />
                    <button type="button" className="commande-label-manager-edit" onClick={() => startEdit(label)}>
                        <strong>{displayedName}</strong>
                        <small>{description}</small>
                    </button>
                    <button
                        type="button"
                        className="commande-label-delete"
                        onClick={() => remove(label)}
                        aria-label={`Supprimer ${displayedName}`}
                        title={`Supprimer ${displayedName}`}
                    >
                        🗑
                    </button>
                </div>
                {isOpen && renderManagedLabels(label.id, depth + 1)}
            </React.Fragment>
        );
    });

    const labelForm = (
        <LabelForm
            editingId={editingId}
            ehpads={ehpads}
            error={error}
            form={form}
            labels={labels}
            onClose={closeModal}
            onReset={resetForm}
            onSubmit={save}
            saving={saving}
            setForm={setForm}
            tournees={tournees}
        />
    );

    return (
        <>
            <aside
                className={`commande-label-sidebar${resizing ? " is-resizing" : ""}`}
                aria-label="Libellés des commandes"
                style={{ width: sidebarWidth, minWidth: sidebarWidth, flexBasis: sidebarWidth }}
            >
                <button
                    type="button"
                    className="commande-label-resizer"
                    aria-label="Redimensionner la colonne des libellés"
                    title="Glissez pour régler la largeur · Double-cliquez pour réinitialiser"
                    onDoubleClick={() => updateSidebarWidth(DEFAULT_SIDEBAR_WIDTH)}
                    onKeyDown={event => {
                        if (event.key === "ArrowLeft") {
                            event.preventDefault();
                            updateSidebarWidth(sidebarWidth - 24);
                        } else if (event.key === "ArrowRight") {
                            event.preventDefault();
                            updateSidebarWidth(sidebarWidth + 24);
                        }
                    }}
                    onPointerDown={event => {
                        resizeStartRef.current = { x: event.clientX, width: sidebarWidth };
                        event.currentTarget.setPointerCapture?.(event.pointerId);
                        setResizing(true);
                    }}
                    onPointerMove={event => {
                        if (!resizeStartRef.current) return;
                        updateSidebarWidth(resizeStartRef.current.width + event.clientX - resizeStartRef.current.x);
                    }}
                    onPointerUp={event => {
                        resizeStartRef.current = null;
                        event.currentTarget.releasePointerCapture?.(event.pointerId);
                        setResizing(false);
                    }}
                    onPointerCancel={() => {
                        resizeStartRef.current = null;
                        setResizing(false);
                    }}
                >
                    <span aria-hidden="true" />
                </button>
                <div className="commande-label-pagination" aria-label="Pagination des commandes">
                    <span>COMMANDES</span>
                    <div>
                        <button
                            aria-label="Page précédente"
                            onClick={() => setPage(previous => Math.max(previous - 1, 0))}
                            disabled={page === 0}
                        >‹</button>
                        <strong>{totalPages === 0 ? "0 / 0" : `${page + 1} / ${totalPages}`}</strong>
                        <button
                            aria-label="Page suivante"
                            onClick={() => setPage(previous => Math.min(previous + 1, totalPages - 1))}
                            disabled={page >= totalPages - 1}
                        >›</button>
                    </div>
                </div>
                <div className="commande-label-heading">
                    <span>LIBELLÉS</span>
                    <button onClick={openCreateModal} aria-label="Ajouter un libellé">＋</button>
                </div>
                {activeKey && (
                    <button className="commande-label-clear-selection" onClick={onClearLabel}>
                        × Réinitialiser le filtre
                    </button>
                )}
                <div className="commande-label-tree">
                    {labels.length === 0
                        ? <p>Aucun libellé partagé</p>
                        : renderLabels()}
                </div>
                <button className="commande-label-manage" onClick={openManageModal}>
                    ＋ Gérer les libellés
                </button>
            </aside>

            {modalMode && (
                <div className="commande-label-modal-backdrop">
                    <button
                        type="button"
                        className="commande-label-modal-close-layer"
                        aria-label="Fermer la fenêtre modale"
                        onMouseDown={closeModal}
                    />
                    <dialog open className={`commande-label-modal commande-label-modal--${modalMode}`} aria-labelledby="commande-label-modal-title">
                        <header>
                            <div>
                                <h2 id="commande-label-modal-title">{modalMode === "create" ? "Créer un libellé" : "Gérer les libellés"}</h2>
                                <p>{modalMode === "create" ? "Ajoutez un dossier ou un filtre partagé à toute la pharmacie." : "Modifiez ou supprimez les libellés partagés de la pharmacie."}</p>
                            </div>
                            <button aria-label="Fermer" onClick={closeModal}>×</button>
                        </header>
                        {modalMode === "create" ? labelForm : (
                            <div className="commande-label-manager-body">
                                <section className="commande-label-manager-list">
                                    <div className="commande-label-manager-title">
                                        <strong>Libellés existants</strong>
                                        <button onClick={openCreateModal}>＋ Nouveau</button>
                                    </div>
                                    {labels.length === 0 && <p className="commande-label-empty">Créez votre premier libellé.</p>}
                                    <div className="commande-label-manager-tree" role="tree" aria-label="Arborescence des libellés">
                                        {renderManagedLabels()}
                                    </div>
                                </section>
                                <div className="commande-label-manager-editor">
                                    {editingId ? labelForm : (
                                        <div className="commande-label-manager-placeholder">
                                            <strong>Sélectionnez un libellé</strong>
                                            <p>Choisissez un libellé dans la liste pour modifier son nom, son contenu, son dossier parent ou sa couleur.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </dialog>
                </div>
            )}
        </>
    );
}
